import type { BoxProps } from "@mui/material/Box";
import Box from "@mui/material/Box";
import { forwardRef, useEffect, useState } from "react";

export type PublicSvgIconProps = BoxProps & {
  name: string;
  path?: string;
  inheritColor?: boolean;
  colorOverride?: string;
  size?: number | string;
};

export const PublicSvgIcon = forwardRef<HTMLSpanElement, PublicSvgIconProps>(
  function PublicSvgIcon(
    { name, path, style, sx, inheritColor, colorOverride, size, ...rest },
    ref,
  ) {
    const [svgContent, setSvgContent] = useState<string>("");
    const src = path || `/material_symbols/${name}.svg`;

    useEffect(() => {
      const loadSvg = async () => {
        try {
          const response = await fetch(src);
          if (response.ok) {
            const svgText = await response.text();
            setSvgContent(svgText);
          }
        } catch (error) {
          console.error("Failed to load SVG:", error);
        }
      };

      loadSvg();
    }, [src]);

    if (!svgContent) {
      return null;
    }

    return (
      <Box
        component="span"
        ref={ref}
        sx={[
          {
            "& svg, & svg *": {
              pointerEvents: "none",
              ...(inheritColor || !!colorOverride
                ? { fill: "currentColor" }
                : {}),
            },
            ...(inheritColor
              ? {
                  "& svg path, & svg circle, & svg polygon": {
                    paintOrder: "stroke fill",
                    stroke: "currentColor",
                    strokeWidth: "var(--svg-icon-stroke, 0)",
                  },
                }
              : {}),
            ...(size !== undefined
              ? { "& svg": { width: size, height: size } }
              : {}),
          },
          ...(colorOverride ? [{ color: colorOverride }] : []),
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
        style={{
          display: "inline-flex",
          color: "inherit",
          fontWeight: "inherit",
          ...style,
        }}
        dangerouslySetInnerHTML={{ __html: svgContent }}
        {...rest}
      />
    );
  },
);
